/**
 * Post-process HTML to detect `/plan execute <project>` in agent messages
 * and make them interactive (tap = execute, long-press = send to another session).
 *
 * Detects patterns like:
 *   Run /plan execute voice-assistant when ready…
 *   `/plan execute voice-assistant`   ← inline code variant
 *
 * Only `/plan execute` is matched — other slash commands are left as-is.
 * Must run AFTER renderMarkdown() and linkifyFilePaths().
 */

/**
 * Common English words that are NOT slash command arguments.
 * Used to stop greedy argument capture at natural sentence boundaries.
 */
const STOP_WORDS = new Set([
  'when', 'where', 'to', 'and', 'or', 'if', 'in', 'on', 'at', 'by', 'for',
  'with', 'from', 'then', 'after', 'before', 'as', 'that', 'this', 'the',
  'a', 'an', 'is', 'are', 'was', 'will', 'you', 'can', 'your', 'our', 'their',
  'ready', 'start', 'now', 'already', 'soon', 'just', 'only', 'also', 'via',
  'run', 'using', 'use', 'see', 'check', 'want', 'need', 'once', 'later',
  'its', 'has', 'have', 'do', 'be', 'go', 'get', 'set', 'let', 'yes', 'no',
]);

/**
 * Only matches `/plan execute` — the specific pattern the user intended to be interactive.
 * Not preceded by / or word character (avoids matching file path segments).
 */
const SLASH_START_RE = /(?<![/\w])\/plan execute\b/g;

/**
 * Given a text string and the end position of a matched /command,
 * greedily consume additional space-separated "slug" tokens that look
 * like command arguments (not English stop words).
 *
 * Returns the full command string including captured arguments.
 */
function extractFullCommand(text: string, cmdStart: number, cmdEnd: number): string {
  let cmd = text.slice(cmdStart, cmdEnd);
  let pos = cmdEnd;

  while (pos < text.length) {
    // Expect a single space separator
    if (text[pos] !== ' ') break;

    // Find the extent of the next token.
    // Note: '.' is intentionally NOT a stop char here so filenames like
    // `model-avatar-redesign.md` are captured whole. A bare trailing period
    // (sentence terminator) is stripped by the guard below.
    let end = pos + 1;
    while (
      end < text.length &&
      text[end] !== ' ' &&
      text[end] !== '\n' &&
      text[end] !== ',' &&
      text[end] !== ';' &&
      text[end] !== ':' &&
      text[end] !== '!' &&
      text[end] !== '?' &&
      text[end] !== '"' &&
      text[end] !== "'" &&
      text[end] !== '(' &&
      text[end] !== ')'
    ) {
      end++;
    }

    const token = text.slice(pos + 1, end);
    if (!token) break;

    // Guard: strip a bare trailing period (sentence terminator, not a file extension).
    // A real extension matches /\.[a-zA-Z]{1,4}$/ (e.g. ".md", ".ts", ".yaml").
    const cleanToken =
      token.endsWith('.') && !/\.[a-zA-Z]{1,4}$/.test(token)
        ? token.slice(0, -1)
        : token;

    // Skip if it's a stop word or doesn't look like a slug/command token
    if (
      !cleanToken ||
      STOP_WORDS.has(cleanToken.toLowerCase()) ||
      !/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(cleanToken)
    ) {
      break;
    }

    cmd += ' ' + cleanToken;
    // Rewind 1 char if we stripped a trailing "." so it isn't consumed as whitespace
    pos = end - (cleanToken.length < token.length ? 1 : 0);
  }

  return cmd;
}

/**
 * Detect a numbered plan-selection list rendered by the agent when no current-plan.md
 * is found. Pattern: an <ol> whose items are all .md filenames, followed (anywhere in
 * the same message) by "Which one?".
 *
 * Each <li> is converted to a tappable button. Clicking sends the selection text
 * (e.g. "17. model-avatar-redesign.md") directly to the active session via the
 * `execute-slash-cmd` custom event — no "Here / Elsewhere" popover needed.
 */
const WHICH_ONE_RE = /which\s+one\b/i;
const PLAN_FILENAME_RE = /^[\w][\w-]*\.md$/i;

export function linkifyPlanChoices(html: string): string {
  if (!WHICH_ONE_RE.test(html)) return html;

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild!;

  const lists = Array.from(root.querySelectorAll('ol'));
  let changed = false;

  for (const ol of lists) {
    const items = Array.from(ol.querySelectorAll(':scope > li'));
    if (items.length < 2) continue;

    // Require ≥ two-thirds of items to look like plan filenames (*.md)
    const planCount = items.filter(
      li => PLAN_FILENAME_RE.test((li.textContent || '').trim())
    ).length;
    if (planCount < Math.ceil(items.length * 0.67)) continue;

    // Transform each <li> into a tappable button row
    items.forEach((li, idx) => {
      const filename = (li.textContent || '').trim();
      const cmd = `${idx + 1}. ${filename}`;
      const btn = doc.createElement('button');
      btn.className = 'plan-choice-btn';
      btn.setAttribute('data-command', cmd);
      btn.setAttribute('tabindex', '0');
      btn.setAttribute('role', 'option');
      btn.setAttribute('title', `Select: ${filename}`);
      const numSpan = doc.createElement('span');
      numSpan.className = 'plan-choice-num';
      numSpan.textContent = String(idx + 1);
      const nameSpan = doc.createElement('span');
      nameSpan.className = 'plan-choice-name';
      nameSpan.textContent = filename;
      btn.appendChild(numSpan);
      btn.appendChild(nameSpan);
      li.textContent = '';
      li.appendChild(btn);
    });

    ol.style.listStyle = 'none';
    ol.style.paddingLeft = '0';
    changed = true;
  }

  return changed ? root.innerHTML : html;
}

/**
 * Process rendered HTML and wrap slash commands with interactive spans.
 * Skips content inside <a>, <code>, <pre> tags.
 */
export function linkifySlashCommands(html: string): string {
  // Quick bail-out: no slash commands possible
  if (!html.includes('/')) return html;

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild!;

  // Pass 1: Walk text nodes (plain text slash commands)
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const tag = parent.tagName.toLowerCase();
      if (tag === 'a' || tag === 'code' || tag === 'pre') return NodeFilter.FILTER_REJECT;
      if (parent.closest('pre')) return NodeFilter.FILTER_REJECT;
      if (parent.closest('.slash-cmd')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    textNodes.push(node as Text);
  }

  for (const textNode of textNodes) {
    const text = textNode.textContent || '';
    SLASH_START_RE.lastIndex = 0;
    if (!SLASH_START_RE.test(text)) continue;

    SLASH_START_RE.lastIndex = 0;
    const frag = doc.createDocumentFragment();
    let lastIdx = 0;
    let match: RegExpExecArray | null;

    while ((match = SLASH_START_RE.exec(text)) !== null) {
      const cmdStart = match.index;
      const cmdEnd = match.index + match[0].length;
      const fullCmd = extractFullCommand(text, cmdStart, cmdEnd);

      if (match.index > lastIdx) {
        frag.appendChild(doc.createTextNode(text.slice(lastIdx, match.index)));
      }

      const span = doc.createElement('span');
      span.className = 'slash-cmd';
      span.setAttribute('data-command', fullCmd);
      span.setAttribute('tabindex', '0');
      span.setAttribute('role', 'button');
      span.setAttribute('title', `Tap to choose: run here or send elsewhere`);
      span.textContent = fullCmd;

      frag.appendChild(span);
      lastIdx = match.index + fullCmd.length;
      // Advance regex past the full command we consumed
      SLASH_START_RE.lastIndex = lastIdx;
    }

    if (lastIdx < text.length) {
      frag.appendChild(doc.createTextNode(text.slice(lastIdx)));
    }

    if (lastIdx > 0) {
      textNode.replaceWith(frag);
    }
  }

  // Pass 2: Inline <code> elements whose entire content is a slash command
  // e.g. `/plan execute voice-assistant`
  const codeEls = Array.from(root.querySelectorAll('code'));
  for (const code of codeEls) {
    if (code.closest('pre') || code.closest('a') || code.closest('.slash-cmd')) continue;
    const text = (code.textContent || '').trim();
    SLASH_START_RE.lastIndex = 0;
    const match = SLASH_START_RE.exec(text);
    if (!match || match.index !== 0) continue;

    // Only process if the code element looks entirely like a slash command
    const fullCmd = extractFullCommand(text, match.index, match.index + match[0].length);
    // Must cover a substantial portion (allow trailing punctuation to be ignored)
    if (!fullCmd) continue;

    const span = doc.createElement('span');
    span.className = 'slash-cmd slash-cmd-code';
    span.setAttribute('data-command', fullCmd);
    span.setAttribute('tabindex', '0');
    span.setAttribute('role', 'button');
    span.setAttribute('title', `Tap to choose: run here or send elsewhere`);
    span.textContent = text; // show original code text
    code.replaceWith(span);
  }

  return root.innerHTML;
}
